import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseApiError, permissions } from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryKey } from '../../data/use-api-suspense-query';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';

interface UpdateRoleVars {
  roleId: number;
  name?: string;
  isActive?: boolean;
}

/**
 * Wraps the create-role and rename/activate/deactivate mutations with the
 * shared roles-list cache invalidation and translated toast handling
 * (409 duplicate code, 422 immutable role, 403 missing permission all render
 * via `apiErrorToUserMessage(parseApiError(err))`). Callers own the
 * imperative call sites.
 */
export function useRoleCrud() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  function invalidateRoles() {
    void queryClient.invalidateQueries({ queryKey: apiQueryKey(permissions.getRoles) });
  }

  const createRoleMutation = useMutation({
    mutationFn: ({ name, code }: { name: string; code: string }) =>
      api.call(permissions.createRole, { name, code }),
    onSuccess: invalidateRoles,
    onError: (err) => toast.error(apiErrorToUserMessage(parseApiError(err), 'mutation')),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, name, isActive }: UpdateRoleVars) =>
      api.call(permissions.updateRole, { id: roleId, name, isActive }),
    onSuccess: invalidateRoles,
    onError: (err) => toast.error(apiErrorToUserMessage(parseApiError(err), 'mutation')),
  });

  return { createRoleMutation, updateRoleMutation };
}
